import { defineStore } from 'pinia';

import { useCompaniesStore } from '@/features/Companies/store';
import type {
  CompanyDistributionSettings,
  DistributionChannel,
  DistributionPool,
} from '@/features/Distribution/types';
import { DISTRIBUTION_WAY, POOL } from '@/features/Distribution/types';
import { buildEmptyDistributionSettings } from '@/features/Distribution/utils';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';

import type { BaseTariff } from '@/features/Tariffs/types';
import { useDistributionSelection } from '@/features/Distribution/useDistributionSelection';

type DistributionStoreState = ResourceContainer<{
  [k: string]: CompanyDistributionSettings;
}>;

export const useDistributionStore = defineStore('DistributionStore', {
  state: (): DistributionStoreState => ({
    data: {},
    isLoading: false,
  }),

  actions: {
    async loadDistributionOptions(
      insuranceId: string,
      calculationId: string,
      _recommendedTariff: BaseTariff | null = null,
      isEVBContext = false,
    ): Promise<void> {
      try {
        this.isLoading = true;

        const api = useApi().distribution;
        const [, resultPromise] = api.loadDistributionOptions(
          insuranceId,
          calculationId,
        );

        if (this.data[insuranceId] === undefined) {
          this.data[insuranceId] = buildEmptyDistributionSettings();
        }

        const result = await resultPromise;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'distribution -> loadDistributionOptions',
          });
        }

        this.data[insuranceId].channels = result.vertriebswege.map(
          (channel) => {
            return {
              ...channel,
              vermittlernummern: channel.vermittlernummern.map(
                (brokerId, index) => ({
                  ...brokerId,
                  isSelected: index === 0,
                }),
              ),
            };
          },
        );

        this.data[insuranceId].pools = result.pools
          .map((pool) => {
            return {
              ...pool,
              vermittlernummern: pool.vermittlernummern.map(
                (brokerId, index) => ({
                  ...brokerId,
                  isSelected: index === 0,
                }),
              ),
            };
          })
          .filter((pool) => {
            if (isEVBContext) {
              // no eVB with JDC
              if (pool.name === POOL.JDC) return false;

              // no Erstantrag for FF
              if (
                pool.name === POOL.FONDS_FINANZ &&
                pool.vermittlernummern.length === 0
              )
                return false;
            }

            return true;
          });

        // Needed double check because pooling is the default value
        const { isPoolingPossible, brokerIdForCompany } =
          useDistributionSelection(insuranceId);

        this.data[insuranceId].selection.method =
          !isPoolingPossible.value || brokerIdForCompany.value !== null
            ? DISTRIBUTION_WAY.USE_COMPANY
            : DISTRIBUTION_WAY.USE_POOL;

        this.fillMissingCompanies(insuranceId);
        this.selectDefaultChannel(insuranceId);
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.DISTRIBUTION_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - InsurenceId: ${insuranceId}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async saveChannelSelection(insuranceId: string) {
      try {
        this.isLoading = true;

        const selectedChannel = this.data[insuranceId].selection.channel;

        if (selectedChannel === null || selectedChannel === insuranceId) {
          return;
        }

        const api = useApi().distribution;
        const [, resultPromise] = api.saveChannelSelection(
          insuranceId,
          selectedChannel,
        );
        await resultPromise;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.DISTRIBUTION_SAVING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - InsurenceId: ${insuranceId}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async callPoolingCallback(insuranceId: string) {
      const { method, pool } = this.data[insuranceId].selection;

      if (pool === null || method !== DISTRIBUTION_WAY.USE_POOL) {
        return;
      }

      const selectedPool = this.getSelectedPool(insuranceId, pool);

      try {
        this.isLoading = true;

        if (selectedPool) {
          const api = useApi().distribution;

          if (selectedPool.links.callback !== undefined) {
            const [, resultPromise] = api.callPoolingCallbackUrl(
              selectedPool.links.callback,
            );

            await resultPromise;
          }
        }
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.DISTRIBUTION_POOL_CALLBACK_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)} - Pool: ${
            selectedPool?.name ?? 'not found'
          } - Callback: ${selectedPool?.links.callback ?? 'not found'}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Their might be the case where we get a company
     * as channel that isn't in our companies store yet.
     * We add it to our companies store with this.
     * @param insuranceId
     */
    fillMissingCompanies(insuranceId: string) {
      if (this.data[insuranceId] !== undefined) {
        const companiesStore = useCompaniesStore();
        const companiesByInsuranceIds = companiesStore.byInsuranceId;

        const existingInsuranceIds = Object.keys(companiesByInsuranceIds);

        this.data[insuranceId].channels.forEach((channel) => {
          const found = existingInsuranceIds.find(
            (id) => id === channel.insuranceId,
          );
          if (found === undefined) {
            companiesStore.data.push({
              insuranceId: channel.insuranceId,
              logoUrl: channel.links.logo,
              name: channel.name,
              isDirektvereinbarung: true,
              supports: {
                api: true,
                tarife: false,
                memo: false,
              },
              isBlacklisted: false,
              meta: null,
            });
          }
        });
      }
    },

    selectDefaultChannel(insuranceId: string) {
      if (
        this.data[insuranceId] !== undefined &&
        this.data[insuranceId].channels.length === 1
      ) {
        this.data[insuranceId].selection.channel =
          this.data[insuranceId].channels[0].insuranceId;
      }
    },

    getPoolForSelectedTariff(tariff: BaseTariff): DistributionPool | undefined {
      const { selectedPoolId } = useDistributionSelection(
        tariff.gesellschaft.id,
      );

      return selectedPoolId.value === null
        ? undefined
        : useDistributionStore().getSelectedPool(
            tariff.gesellschaft.id,
            selectedPoolId.value,
          );
    },
  },

  getters: {
    getDistributionSettings:
      (state) =>
      (insuranceId: string): CompanyDistributionSettings | undefined =>
        state.data[insuranceId],

    hasBrokerIdForChannel:
      (state) =>
      (insuranceId: string, channelId: string): boolean => {
        if (state.data[insuranceId] === undefined || state.isLoading) {
          return false;
        }

        return state.data[insuranceId].channels.reduce(
          (hasBrokerId, channel) => {
            if (hasBrokerId) {
              return hasBrokerId;
            }

            return (
              channel.insuranceId === channelId &&
              channel.vermittlernummern.length > 0
            );
          },
          false as boolean,
        );
      },

    hasBrokerId(): (insuranceId: string) => boolean {
      return (insuranceId: string): boolean =>
        this.hasBrokerIdForChannel(insuranceId, insuranceId);
    },

    getBrokerIdForChannel:
      (state) =>
      (insuranceId: string, selectedChannel: string): string | null => {
        const filtered = state.data[insuranceId].channels.filter(
          (channel) =>
            channel.insuranceId === selectedChannel &&
            channel.vermittlernummern.length > 0,
        );

        if (filtered.length === 1) {
          return (
            filtered[0].vermittlernummern.find(
              (brokerId) => brokerId.isSelected,
            )?.vermittlernummer ?? null
          );
        }

        return null;
      },

    getIsBrokerIdVerkettetForChannel:
      (state) =>
      (insuranceId: string, selectedChannel: string): boolean => {
        const filtered = state.data[insuranceId].channels.filter(
          (channel) =>
            channel.insuranceId === selectedChannel &&
            channel.vermittlernummern.length > 0,
        );

        if (filtered.length === 1) {
          return (
            filtered[0].vermittlernummern.find(
              (brokerId) => brokerId.isSelected,
            )?.isVerkettet ?? false
          );
        }

        return false;
      },

    getBrokerIdForPool:
      (state) =>
      (insuranceId: string, poolName: string): string | null => {
        const filtered = state.data[insuranceId].pools.filter(
          (pool) => pool.name === poolName && pool.vermittlernummern.length > 0,
        );

        if (filtered.length === 1) {
          return (
            filtered[0].vermittlernummern.find(
              (brokerId) => brokerId.isSelected,
            )?.vermittlernummer ?? null
          );
        }

        return null;
      },

    hasBrokerIdOnDifferentChannel:
      (state) =>
      (insuranceId: string): boolean => {
        if (state.data[insuranceId] === undefined || state.isLoading) {
          return false;
        }

        return state.data[insuranceId].channels.reduce(
          (hasBrokerId, channel) => {
            if (hasBrokerId) {
              return hasBrokerId;
            }
            return (
              channel.insuranceId !== insuranceId &&
              channel.vermittlernummern.length > 0
            );
          },
          false as boolean,
        );
      },

    getOtherDistributionChannels(
      state,
    ): (insuranceId: string) => DistributionChannel[] {
      return (insuranceId: string): DistributionChannel[] => {
        if (
          state.isLoading ||
          !this.hasBrokerIdOnDifferentChannel(insuranceId)
        ) {
          return [];
        }

        return state.data[insuranceId].channels.filter(
          (channel) => channel.insuranceId !== insuranceId,
        );
      };
    },

    getSelectedPool:
      (state) =>
      (insuranceId: string, poolName: string): DistributionPool | undefined =>
        state.data[insuranceId].pools.find((pool) => pool.name === poolName),

    getSelectedChannel:
      (state) =>
      (
        insuranceId: string,
        channelName: string,
      ): DistributionChannel | undefined =>
        state.data[insuranceId].channels.find(
          (channel) => channel.insuranceId === channelName,
        ),

    canSwitchDistributionSelection:
      (state) =>
      (insuranceId: string): boolean => {
        return state.data[insuranceId].pools.length > 0;
      },
  },
});
